import React, { useCallback, useState } from 'react';
import { Box, FormControlLabel, Grid, Icon, IconButton, InputAdornment, Link, Radio, RadioGroup, TextField, Typography, useTheme } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useAuthContext } from '../../../shared/contexts';
import { LayoutSignin } from '../../../shared/layouts';
import { applicationInfo, environment } from '../../../shared/environment';
import { GenericService, UserService } from '../../../shared/services';
import { ISnackbarAlert, SnackbarAlert } from '../../../shared/components';
import { Result, UserDTO } from '../../../shared/interfaces';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n';

const env = environment();

export const Signin: React.FC = () => {
    const theme = useTheme();
    const { handleSignIn } = useAuthContext();
    const [userName, setUserName] = useState('');
    const [password, setPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [snackBarAlert, setSnackBarAlert] = useState<ISnackbarAlert>();
    const [isOpenMessage, setIsOpenMessage] = useState(false);
    const [isFirstAccess, setIsFirstAccess] = useState(false);
    const [userDTO, setUserDTO] = useState<UserDTO>();
    const { t } = useTranslation();

    function Copyright() {
        return (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 5 }}>
                {'Copyright © '}
                <Link color='inherit' href={applicationInfo.applicationUrl}>
                    {applicationInfo.applicatioName + ' - ' + applicationInfo.organizationName}
                </Link>{' '}
                {new Date().getFullYear()}
                {'.'}
            </Typography>
        );
    }

    function EnvironmentInfo() {
        const env = environment();

        if (!env.name || env.name === 'Production') {
            return null;
        }

        return (
            <Typography variant="body2" color="text.primary" align="center" sx={{ mt: 5 }}>
                {env.name}
            </Typography>
        );
    }

    const handleAuthenticate = useCallback(async (userName: string, password: string) => {
        try {
            setLoading(true);

            const user = {
                userName: userName,
                password: password
            };
            const userResult: Result<UserDTO> = await UserService.AuthenticateAsync(user);

            if (userResult instanceof Error) { throw new Error(userResult.message); }

            if (userResult.code === 200 && userResult.value !== null) {

                const userInfo = {
                    user: {
                        id: userResult.value.id,
                        userName: userResult.value.userName,
                        peopleId: userResult.value.peopleId,
                        isFirstAccess: userResult.value.isFirstAccess,
                        lastAccessDateTime: userResult.value.lastAccessDateTime,
                        companyId: userResult.value.companyId,
                        token: userResult.value.token,
                        tokenType: userResult.value.tokenType,
                        refleshToken: userResult.value.refleshToken,
                        tokenExpirationDateTime: userResult.value.tokenExpirationDateTime,
                        tokenCreationDateTime: userResult.value.tokenCreationDateTime
                    }
                };
                if (userResult.value.isFirstAccess) {
                    setIsFirstAccess(true);
                }
                else {
                    setIsFirstAccess(false);
                    handleSignIn(userInfo);
                }
            }
            else {
                showMessage(userResult.info, 'info');
            }
            setLoading(false);
        } catch (error) {
            showMessage(String(error), 'error');

            setLoading(false);
        }
    }, [userName]);

    const handleUpdatePassword = useCallback(async (userName: string, password: string, newPassword: string, confirmPassword: string) => {
        if(newPassword == confirmPassword) {
            const dto: UserDTO = {
                userName: userName,
                password: password,
                newPassword: newPassword,
                confirmPassword: confirmPassword,
            };
            GenericService.PostGenericAsync1('authsphere.user', 'ChangePasswordAsync', dto, () => void undefined, setLoading, showMessage, setUserDTO);

            handleAuthenticate(userName, newPassword);
        }
        else{
            showMessage(t('login.message'), 'error');
        }
    }, [newPassword]);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if(isFirstAccess) {
            handleUpdatePassword(userName, password, newPassword, confirmPassword);
        }
        else {
            handleAuthenticate(userName, password);
        }
    };

    const handleChangeLanguage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const language = (event.target as HTMLInputElement).value;
        i18n.changeLanguage(language);
    };

    const passwordAdornment = (isVisible: boolean, onToggle: () => void) => (
        <InputAdornment position="end">
            <IconButton
                aria-label={isVisible ? 'Ocultar senha' : 'Mostrar senha'}
                edge="end"
                onClick={onToggle}
                onMouseDown={(event) => event.preventDefault()}
                size="small"
            >
                <Icon>{isVisible ? 'visibility_off' : 'visibility'}</Icon>
            </IconButton>
        </InputAdornment>
    );

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        type NewType = ISnackbarAlert;
        const msg: NewType = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarAlert(msg);
        setIsOpenMessage(true);
    }
    return (
        <React.Fragment>
            <LayoutSignin>
                <SnackbarAlert
                    snackBarAlert={snackBarAlert}
                    setIsOpen={setIsOpenMessage}
                    isOpen={isOpenMessage}
                    variant={'filled'}
                />
                <Box
                    sx={{
                        my: 8,
                        mx: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        component='img'
                        alt='MTP'
                        src={theme.palette.mode === 'dark' ? env.logoLight : env.logo}
                        sx={{
                            width: 96,
                            height: 42,
                            mb: 2,
                            objectFit: 'contain',
                        }}
                    />
                    <Typography component="h1" variant="h5">
                        {applicationInfo.applicatioName}
                    </Typography>
                    <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1 }}>
                        {isFirstAccess ? (
                            <React.Fragment>
                                <Typography component="h1" variant="h6">
                                    {t('login.changePassword')}
                                </Typography>
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    name="newPassword"
                                    label={t('login.newPassword')}
                                    type={showNewPassword ? 'text' : 'password'}
                                    id="newPassword"
                                    autoComplete="current-password"
                                    size='small'
                                    value={newPassword}
                                    onChange={(event) => setNewPassword(event.target.value)}
                                    autoFocus={true}
                                    InputProps={{
                                        endAdornment: passwordAdornment(showNewPassword, () => setShowNewPassword((value) => !value)),
                                    }}
                                />
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    name="confirmPassword"
                                    label={t('login.confirmPassword')}
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    id="confirmPassword"
                                    autoComplete="current-password"
                                    size='small'
                                    value={confirmPassword}
                                    onChange={(event) => setConfirmPassword(event.target.value)}
                                    InputProps={{
                                        endAdornment: passwordAdornment(showConfirmPassword, () => setShowConfirmPassword((value) => !value)),
                                    }}
                                />
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="userName"
                                    label={t('login.userName')}
                                    placeholder={t('login.userName')}
                                    name="userName"
                                    autoComplete="userName"
                                    autoFocus
                                    size='small'
                                    value={userName}
                                    onChange={(event) => setUserName(event.target.value)}
                                />
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    name="password"
                                    label={t('login.password')}
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    autoComplete="current-password"
                                    size='small'
                                    value={password}
                                    onChange={(event) => setPassword(event.target.value)}
                                    InputProps={{
                                        endAdornment: passwordAdornment(showPassword, () => setShowPassword((value) => !value)),
                                    }}
                                />
                            </React.Fragment>
                        )}
                        {/* <FormControlLabel
                            control={<Checkbox value="remember" color="primary" size='small' />}
                            label="Lembre me"
                        /> */}
                        <LoadingButton
                            type='submit'
                            fullWidth
                            variant="contained"
                            size="medium"
                            sx={{ mt: 2, mb: 2 }}
                            startIcon={<Icon>lock_open_outlined</Icon>}
                            loading={loading}
                            loadingPosition="start"
                        >
                            {t('login.submit')}
                        </LoadingButton>
                        <Grid container>
                            <Grid item>
                                <RadioGroup
                                    row
                                    aria-labelledby="language-selector-group-label"
                                    name="language-selector-group"
                                    value={i18n.language}
                                    onChange={handleChangeLanguage}
                                >
                                    <FormControlLabel value="en" control={<Radio size='small' />} label="English" />
                                    <FormControlLabel value="pt" control={<Radio size='small' />} label="Português" />
                                    <FormControlLabel value="es" control={<Radio size='small' />} label="Español" />
                                </RadioGroup>
                            </Grid>
                        </Grid>
                        <Copyright />
                        <EnvironmentInfo />
                    </Box>
                </Box>
            </LayoutSignin>
        </React.Fragment>
    );
};
